// `extension-alert-candidates-ats-info` — the "Bullhorn information" card on the candidate
// profile: who owns the record, what status it is in, when it was added, and when a CV last
// landed on it, over the latest note the ATS holds.
//
// ⚠️ EVERY VISIBLE PIECE OF STATE ON THIS CARD IS DECIDED BY ONE SESSION FLAG, `inATS`, and it
// decides them in OPPOSITE directions:
//
//   collapseAtsInfo = inATS     → framed inside the ATS, the card starts COLLAPSED
//   showViewInAts   = !inATS    → …and the header's "View in ATS" link is HIDDEN
//
// The reasoning is that a user already looking at the ATS needs neither. And `inATS` is TRUE by
// default rather than false: `hostsConsideredATS` includes `ShortlistSessionHost.None`, so a
// frame whose shell never answered the host-info message — a plain browser tab, which is what
// every baseline capture is — counts as in-ATS. Defaulting this prop to `false` would open the
// card and add a link that production does not show.
import { useState, type ReactElement } from 'react'
import { TextReadMore } from '../common/TextReadMore.tsx'
import { displayDate } from '../common/displayDate.ts'

/** `IAlertCandidateAtsNoteDto`. */
export interface AtsNote {
  dateAdded?: number | null
  submitterName?: string | null
  text: string
}

/** `IAlertCandidateAtsInfoDto`. */
export interface AtsInfo {
  latestStatus?: { text: string; date?: number | null } | null
  ownerFullName?: string | null
  externalCreationDate?: number | null
  externalCandidateNote?: AtsNote | null
}

/**
 * `ATSNoteCopyOptions` / `ATSNoteCopyDefault` (`shortlist.constants.ng1.ts:170`) — the word for
 * the thing the ATS stores against a candidate. Vincere calls it a comment; everyone else calls
 * it a note, so the heading reads "Latest note" or "Latest comment".
 */
const ATS_NOTE_COPY: Readonly<Record<string, string>> = { Bullhorn: 'note', Vincere: 'comment' }
const ATS_NOTE_COPY_DEFAULT = 'note'

export function atsNoteCopy(atsName: string | undefined): string {
  if (atsName === undefined) return ATS_NOTE_COPY_DEFAULT
  return ATS_NOTE_COPY[atsName] ?? ATS_NOTE_COPY_DEFAULT
}

/**
 * `getLatestStatusText` — the status, with its date appended when there is one.
 *
 * Returns undefined with no status at all; the template then falls back to "Unknown", which is
 * the same word an absent owner gets.
 */
export function latestStatusText(atsInfo: AtsInfo): string | undefined {
  const status = atsInfo.latestStatus
  if (status === undefined || status === null) return undefined
  const date = status.date === undefined || status.date === null ? undefined : displayDate(status.date)
  return date === undefined ? status.text : `${status.text}, ${date}`
}

/**
 * `getNoteSubtitle` — "16/09/2026 - Sandbox API", or either half alone.
 *
 * ⚠️ THE SEPARATOR BELONGS TO THE NAME, NOT TO THE JOIN. Upstream builds the submitter half as
 * `" - " + name` only when a date string exists, so a note with a submitter and no date reads
 * "Sandbox API" with no leading dash — which is why this cannot be written as a join over a
 * filtered array.
 */
export function noteSubtitle(note: AtsNote | undefined | null): string | undefined {
  if (note === undefined || note === null) return undefined
  const hasDate = note.dateAdded !== undefined && note.dateAdded !== null && note.dateAdded !== 0
  const hasSubmitter = note.submitterName !== undefined && note.submitterName !== null && note.submitterName !== ''
  if (!hasDate && !hasSubmitter) return undefined

  const dateString = hasDate ? (displayDate(note.dateAdded) ?? '') : ''
  const submitterString = !hasSubmitter ? '' : dateString !== '' ? ` - ${String(note.submitterName)}` : String(note.submitterName)
  return `${dateString}${submitterString}`
}

export interface AtsInfoCardProps {
  atsInfo: AtsInfo
  atsName?: string
  /**
   * `getAtsLabel($window)` — the ATS the card NAMES (5cfd3ef899). `atsName` stays the key of the
   * note-copy table. Defaults to `atsName`.
   */
  atsLabel?: string
  /** `candidateAlertInfo.cvDate` — epoch millis; the card's fourth cell, not part of `atsInfo`. */
  cvDate?: number | null
  /** See the note at the top: TRUE is the default a session with no shell reports. */
  inAts?: boolean
  onViewInAts?: () => void
}

export function AtsInfoCard({
  atsInfo,
  atsName,
  atsLabel = atsName,
  cvDate,
  inAts = true,
  onViewInAts,
}: AtsInfoCardProps): ReactElement {
  const [collapsed, setCollapsed] = useState(inAts)
  const note = atsInfo.externalCandidateNote ?? undefined
  const status = latestStatusText(atsInfo)
  const subtitle = noteSubtitle(note)

  return (
    <div className="extension-candidates-ats-info-content">
      <div className="snippet-section">
        <div className="snippet-section-header ats-info-header">
          <div>
            {/* "Bullhorn information" — the tenant's ATS name, verbatim from the bootstrap. */}
            <strong>{atsLabel} information</strong>
          </div>
          {inAts ? null : (
            <div className="ats-info-header-view-in-ats">
              <span
                role="button"
                tabIndex={0}
                onClick={onViewInAts}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onViewInAts?.()
                }}
              >
                View in ATS
              </span>
            </div>
          )}
        </div>
        <div className="snippet-section-body ats-info-body">
          <div className={`ats-info-collapsible-container${collapsed ? ' collapse-ats-info' : ''}`}>
            {note === undefined ? null : (
              <div className="ats-info-note">
                <div className="ats-info-note-title">Latest {atsNoteCopy(atsName)}</div>
                <div className="ats-info-note-subtitle">{subtitle}</div>
                <div className="ats-info-note-content">
                  {/* `display-as-html="false"` — this is text a recruiter typed into the ATS. */}
                  <TextReadMore text={note.text} lineLimit={5} />
                </div>
              </div>
            )}
            <div className="ats-info-details">
              {/* Two empty divs upstream; the grid template sizes its columns off them. */}
              <div className="ats-info-col-one" />
              <div className="ats-info-col-two" />
              <div className="ats-info-details-row">
                <div className="ats-info-details-cell">
                  <div className="ats-info-details-title">Owner</div>
                  <div className="ats-info-details-text ats-info-owner-name">
                    {atsInfo.ownerFullName === undefined ||
                    atsInfo.ownerFullName === null ||
                    atsInfo.ownerFullName === ''
                      ? 'Unknown'
                      : atsInfo.ownerFullName}
                  </div>
                </div>
                <div className="ats-info-details-cell">
                  <div className="ats-info-details-title">Status</div>
                  <div className="ats-info-details-text ats-info-latest-status">{status ?? 'Unknown'}</div>
                </div>
              </div>
              <div className="ats-info-details-row">
                <div className="ats-info-details-cell">
                  <div className="ats-info-details-title">Added to {atsLabel}</div>
                  <div className="ats-info-details-text">{displayDate(atsInfo.externalCreationDate) ?? 'Unknown'}</div>
                </div>
                <div className="ats-info-details-cell">
                  <div className="ats-info-details-title">Latest CV added</div>
                  <div className="ats-info-details-text">{displayDate(cvDate) ?? 'Unknown'}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="view-more-less-cont">
            {/* One word, two states — and note that it is "Expand"/"Collapse" here while the
                snippet header a few pixels above says "More"/"Less". */}
            <span
              className={`view-more-less ${collapsed ? 'view-more' : 'view-less'}`}
              role="button"
              tabIndex={0}
              onClick={() => {
                setCollapsed(!collapsed)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setCollapsed(!collapsed)
              }}
            >
              {collapsed ? 'Expand ' : 'Collapse '}
              <i className={collapsed ? 'fas fa-angle-down' : 'fas fa-angle-up'} />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
